import axios from 'axios'
import { getApiErrorData } from '_metronic/helpers/AlertHelper'
import { TFunction } from '_metronic/i18n/Metronici18n'
import { SNCR_CODIGO } from './_models'
import { SNCR_ESPERA_EXPIROU, SNCR_POPUP_BLOQUEADA, SNCR_POPUP_FECHADA } from './govbr'

export type ErroSncr = {
  /** Código de `SncrNegocioException` (422), quando o erro veio do backend com um. */
  codigo?: string
  status?: number
  /** `message` do envelope `{data: {message, codigo}}` do backend. */
  mensagem?: string
}

export type ErroTraduzido = { mensagem: string; podeRepetir: boolean }

/** Lê o envelope de erro do backend uma única vez; tudo vazio para erro que não é do axios. */
export const erroSncr = (error: unknown): ErroSncr => {
  if (!axios.isAxiosError(error)) return {}
  const data = getApiErrorData(error)
  return {
    codigo: typeof data?.codigo === 'string' ? data.codigo : undefined,
    status: error.response?.status,
    mensagem: typeof data?.message === 'string' ? data.message : undefined,
  }
}

/** Código de negócio do SNCR num erro do axios, ou `undefined` para qualquer outro erro. */
export const codigoErroSncr = (error: unknown): string | undefined => erroSncr(error).codigo

/**
 * Mensagem para o usuário e se vale tentar de novo, a partir de qualquer erro da retirada:
 * os da janela gov.br (`govbr.ts`), os de negócio do backend (422 com código) e os de transporte.
 */
export const traduzirErroSncr = (error: unknown, t: TFunction): ErroTraduzido => {
  if (error instanceof Error) {
    if (error.message === SNCR_POPUP_BLOQUEADA) {
      return { mensagem: t('sncr.erro.popup_bloqueada'), podeRepetir: true }
    }
    if (error.message === SNCR_POPUP_FECHADA) {
      return { mensagem: t('sncr.erro.popup_fechada'), podeRepetir: true }
    }
    if (error.message === SNCR_ESPERA_EXPIROU) {
      return { mensagem: t('sncr.erro.espera_expirou'), podeRepetir: true }
    }
  }
  const { codigo, status, mensagem } = erroSncr(error)
  if (codigo === SNCR_CODIGO.TOKEN_EXPIRADO || codigo === SNCR_CODIGO.TOKEN_INVALIDO) {
    return { mensagem: t('sncr.erro.token'), podeRepetir: true }
  }
  if (codigo === SNCR_CODIGO.LIMITE_BLOCOS_MES) {
    return { mensagem: mensagem || t('sncr.erro.limite_mes'), podeRepetir: false }
  }
  if (codigo === SNCR_CODIGO.RETIRADA_EM_ANDAMENTO) {
    return { mensagem: t('sncr.erro.retirada_em_andamento'), podeRepetir: true }
  }
  if (codigo === SNCR_CODIGO.SEM_NUMERACAO_DISPONIVEL) {
    // Cota do dia ou cota da Visa: repetir agora dá o mesmo 204.
    return { mensagem: mensagem || t('sncr.erro.sem_numeracao'), podeRepetir: false }
  }
  if (status === 403) {
    // Trocar de conta exige fechar o navegador: nem o logout do Keycloak da Anvisa nem o do gov.br
    // bastaram nos testes de 08/10/2026 (cookies de sessão dos dois lados seguram o login).
    return { mensagem: t('sncr.erro.cpf_diferente'), podeRepetir: false }
  }
  if (status !== undefined && status >= 500) {
    // 503 do backend vem com a causa (ex.: integração não configurada); sem envelope é rede/proxy.
    return { mensagem: mensagem || t('sncr.erro.indisponivel'), podeRepetir: true }
  }
  if (mensagem) {
    return { mensagem, podeRepetir: codigo !== SNCR_CODIGO.INTEGRACAO_DESATIVADA }
  }
  return {
    mensagem: error instanceof Error && error.message ? error.message : t('sncr.erro.inesperado'),
    podeRepetir: true,
  }
}
